/**
 * Timestamps, rendered in the viewer's own time zone.
 *
 * Every timestamp the API returns is an explicitly UTC instant (it ends in `Z` — see
 * `UtcDateTimeConverter`). These helpers are the only place that turns one into text, so
 * the whole application agrees on what a time means.
 *
 * Before this there were four approaches in play: `toLocaleString()` in three screens,
 * an explicit `…Z` suffix in the schedule panel, `toISOString().slice()` in two more, and
 * — worst — the audit log slicing the raw string, which showed a UTC instant with no
 * marker so it read as local and was wrong by the viewer's offset.
 */

/** Date and time, e.g. `01/08/2026, 13:53` in the viewer's locale and zone. */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—'

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return '—'

  return parsed.toLocaleString(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** Date and time to the second, for logs and audit trails where ordering matters. */
export function formatDateTimeSeconds(value: string | null | undefined): string {
  if (!value) return '—'

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return '—'

  return parsed.toLocaleString(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

/**
 * The calendar date of an instant, in the viewer's zone.
 *
 * Not `toISOString().slice(0, 10)`, which gives the UTC date and can be a day out either
 * side of midnight — long enough to make a run look like it happened on the wrong day.
 */
export function formatDate(value: string | null | undefined): string {
  if (!value) return '—'

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return '—'

  return parsed.toLocaleDateString(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
}

/**
 * The viewer's time zone, short form — e.g. `GMT+1`.
 *
 * Shown wherever a time is one somebody might act on, so "03:00" is not ambiguous between
 * the reader's clock and the server's.
 */
export function localZoneLabel(): string {
  const parts = new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' })
    .formatToParts(new Date())

  return parts.find((p) => p.type === 'timeZoneName')?.value ?? ''
}
